import React, { useEffect, useRef, useState } from "react";
import api, { formatErrorDetail, formatDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Surface } from "@/components/app/Primitives";
import { MapPin, Crosshair, Camera, CheckCircle2, AlertTriangle, Loader2, LogOut, Smartphone, Coffee } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const GPS_OPTIONS = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 };

function GpsStatus({ state }) {
    const variants = {
        idle: { color: "text-muted-foreground", icon: Crosshair, label: "Idle" },
        requesting: { color: "text-yellow-600", icon: Loader2, label: "Getting your location…", spin: true },
        ready: { color: "text-emerald-600", icon: CheckCircle2, label: "Ready to clock in" },
        low_accuracy: { color: "text-yellow-600", icon: AlertTriangle, label: "Low accuracy — move to open area" },
        denied: { color: "text-destructive", icon: AlertTriangle, label: "Location permission denied" },
        unavailable: { color: "text-destructive", icon: AlertTriangle, label: "Location unavailable" },
        timeout: { color: "text-destructive", icon: AlertTriangle, label: "GPS timeout" },
    };
    const v = variants[state] || variants.idle;
    const Icon = v.icon;
    return (
        <div className={`flex items-center gap-2 ${v.color}`} data-testid={`gps-status-${state}`}>
            <Icon className={`size-4 ${v.spin ? "animate-spin" : ""}`} />
            <span className="text-sm font-medium">{v.label}</span>
        </div>
    );
}

export default function ClockIn() {
    const { user, companies, activeCompanyId } = useAuth();
    const [policy, setPolicy] = useState(null);
    const [companyName, setCompanyName] = useState("");
    const [attendance, setAttendance] = useState(null);
    const [employeeId, setEmployeeId] = useState("");
    const [gps, setGps] = useState(null);
    const [gpsState, setGpsState] = useState("idle");
    const [gpsError, setGpsError] = useState("");
    const [selfie, setSelfie] = useState(null);
    const [showCam, setShowCam] = useState(false);
    const [busy, setBusy] = useState(false);
    const [wfh, setWfh] = useState(false);
    const videoRef = useRef(null);

    // The logged-in person clocks in — no employee selection.
    useEffect(() => {
        if (user?.employee_id) setEmployeeId(user.employee_id);
    }, [user]);

    // Holiday banner: premium notice or pre-approval warning per HR policy.
    const [holidayToday, setHolidayToday] = useState(null);
    useEffect(() => {
        api.get("/holidays/today").then((r) => setHolidayToday(r.data)).catch(() => {});
    }, [activeCompanyId]);

    useEffect(() => {
        if (!employeeId) return;
        api.get("/timekeeping/clock-status", { params: { employee_id: employeeId } }).then((r) => {
            setPolicy(r.data.policy);
            setAttendance(r.data.attendance);
            setCompanyName(r.data.company_name);
        });
    }, [employeeId, activeCompanyId]);

    const requestGps = () => {
        if (!("geolocation" in navigator)) { setGpsState("unavailable"); setGpsError("Geolocation API not available"); return; }
        setGpsState("requesting");
        setGpsError("");
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const data = {
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                    accuracy: pos.coords.accuracy,
                    ts: new Date(pos.timestamp).toISOString(),
                };
                setGps(data);
                const threshold = policy?.gps_accuracy_threshold_m || 100;
                setGpsState(data.accuracy > threshold ? "low_accuracy" : "ready");
            },
            (err) => {
                const map = { 1: "denied", 2: "unavailable", 3: "timeout" };
                setGpsState(map[err.code] || "unavailable");
                setGpsError(err.message);
            },
            GPS_OPTIONS,
        );
    };

    useEffect(() => { if (policy) requestGps(); /* eslint-disable-next-line */ }, [policy, employeeId]);

    const startCamera = async () => {
        try {
            setShowCam(true);
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 640 }, audio: false });
            if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
        } catch (e) { toast.error("Camera permission denied"); setShowCam(false); }
    };
    const captureSelfie = () => {
        if (!videoRef.current) return;
        const v = videoRef.current;
        const canvas = document.createElement("canvas");
        canvas.width = v.videoWidth; canvas.height = v.videoHeight;
        canvas.getContext("2d").drawImage(v, 0, 0);
        canvas.toBlob((blob) => {
            if (!blob) return;
            const reader = new FileReader();
            reader.onload = () => setSelfie({ blob, dataUrl: reader.result });
            reader.readAsDataURL(blob);
            // Stop camera
            const stream = v.srcObject; if (stream) stream.getTracks().forEach((t) => t.stop());
            setShowCam(false);
        }, "image/jpeg", 0.7);
    };

    const uploadSelfieIfAny = async () => {
        if (!selfie) return null;
        const fd = new FormData();
        fd.append("file", new File([selfie.blob], "selfie.jpg", { type: "image/jpeg" }));
        fd.append("kind", "selfie");
        fd.append("employee_id", employeeId);
        try {
            const r = await api.post("/admin/documents/upload", fd);
            return r.data?.storage_path || null;
        } catch { return null; }
    };

    const submit = async (kind) => {
        if (!employeeId) return;
        setBusy(true);
        try {
            const selfiePath = await uploadSelfieIfAny();
            const payload = {
                employee_id: employeeId,
                gps, source: "web",
                selfie_path: selfiePath,
                ...(kind === "in" ? { work_from_home: wfh } : {}),
            };
            const url = kind === "in" ? "/timekeeping/attendance/check-in" : "/timekeeping/attendance/check-out";
            const r = await api.post(url, payload);
            setAttendance(r.data);
            setSelfie(null);
            toast.success(`Clocked ${kind === "in" ? "in" : "out"} successfully`);
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed");
        } finally { setBusy(false); }
    };

    const breakAction = async (kind) => {
        setBusy(true);
        try {
            const r = await api.post(`/timekeeping/attendance/break-${kind}`, { employee_id: employeeId });
            setAttendance(r.data);
            toast.success(kind === "in" ? "Break started" : "Back from break");
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed");
        } finally { setBusy(false); }
    };

    const accuracy = gps?.accuracy;
    const threshold = policy?.gps_accuracy_threshold_m || 100;
    const ready = gpsState === "ready" || gpsState === "low_accuracy";
    const hasClockedIn = !!attendance?.time_in;
    const hasClockedOut = !!attendance?.time_out;
    const breaks = attendance?.breaks || [];
    const onBreak = breaks.length > 0 && !breaks[breaks.length - 1].end;
    const needsSelfie = !!policy?.selfie_required;
    const selfieReady = !needsSelfie || !!selfie;
    const company = companies?.find((c) => c.id === activeCompanyId);

    return (
        <div className="max-w-2xl mx-auto">
            <PageHeader
                title="Clock In"
                subtitle={`${companyName || company?.name || ""} · GPS-verified attendance`}
            />
            <Surface className="p-5 mb-3">
                <div className="space-y-3">
                    {!user?.employee_id && (
                        <div className="border border-amber-300 bg-amber-50 text-amber-800 rounded-md p-3 text-sm" data-testid="ci-no-employee">
                            Your account isn't linked to an employee record, so there is nothing to clock in.
                            Ask HR to link your user to an employee profile.
                        </div>
                    )}

                    {holidayToday?.holiday && (
                        <div className={`rounded-md p-3 text-sm border ${holidayToday.require_preapproval ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`} data-testid="ci-holiday-banner">
                            <div className="font-semibold">🎌 Today is {holidayToday.holiday.name} — {holidayToday.holiday.holiday_type === "regular" ? "Regular Holiday" : "Special Non-Working Day"}</div>
                            <div className="text-xs mt-0.5">
                                {holidayToday.require_preapproval
                                    ? "Working today requires HR pre-approval. Your clock-in will be marked pending until HR approves it in Attendance Review."
                                    : `Clocking in counts immediately as holiday service — ${holidayToday.holiday.holiday_type === "regular" ? "200% pay" : "+30% premium"} applies automatically in payroll.`}
                            </div>
                        </div>
                    )}

                    <div className="border border-border rounded-md p-3">
                        <div className="flex items-center justify-between">
                            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Location</div>
                            <GpsStatus state={gpsState} />
                        </div>
                        {gps ? (
                            <div className="mt-2 grid grid-cols-2 gap-2 text-xs font-mono" data-testid="gps-readout">
                                <div><span className="text-muted-foreground">Lat:</span> {gps.lat.toFixed(6)}</div>
                                <div><span className="text-muted-foreground">Lng:</span> {gps.lng.toFixed(6)}</div>
                                <div><span className="text-muted-foreground">Accuracy:</span> ±{Math.round(accuracy)}m {accuracy > threshold && <span className="text-yellow-600">(threshold {threshold}m)</span>}</div>
                                <div><span className="text-muted-foreground">Time:</span> {new Date(gps.ts).toLocaleTimeString()}</div>
                            </div>
                        ) : <div className="text-xs text-muted-foreground mt-2">No location captured yet.</div>}
                        {gps && (
                            <div className="mt-3">
                                <iframe
                                    title="map-preview"
                                    className="w-full h-40 rounded-md border border-border"
                                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${gps.lng - 0.003},${gps.lat - 0.0015},${gps.lng + 0.003},${gps.lat + 0.0015}&layer=mapnik&marker=${gps.lat},${gps.lng}`}
                                />
                            </div>
                        )}
                        {gpsState === "low_accuracy" && (
                            <div className="mt-2 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 p-2 rounded-md">
                                <AlertTriangle className="size-3 inline mr-1" /> Location accuracy is low. Please move to an open area or enable high-accuracy location.
                            </div>
                        )}
                        {(gpsState === "denied" || gpsState === "unavailable" || gpsState === "timeout") && (
                            <div className="mt-2 text-xs text-destructive bg-red-50 border border-red-200 p-2 rounded-md">
                                {gpsError || "Unable to get your location."} Please enable location and tap "Retry".
                            </div>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                            <button onClick={requestGps} className="text-xs h-7 px-2 rounded-md border border-border bg-background" data-testid="gps-retry">Retry GPS</button>
                            <div className="text-[11px] text-muted-foreground">Your location will be attached to this attendance log.</div>
                        </div>
                    </div>

                    {needsSelfie && (
                        <div className="border border-border rounded-md p-3">
                            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Selfie required</div>
                            {selfie ? (
                                <div className="flex items-center gap-2">
                                    <img alt="selfie" src={selfie.dataUrl} className="size-20 rounded-md object-cover" />
                                    <button onClick={() => { setSelfie(null); startCamera(); }} className="h-8 px-2 rounded-md border border-border text-xs">Retake</button>
                                </div>
                            ) : (
                                <button onClick={startCamera} className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-2" data-testid="open-camera-btn"><Camera className="size-4" /> Open Camera</button>
                            )}
                        </div>
                    )}

                    {policy?.geofence?.enabled && (
                        <div className="text-xs text-muted-foreground border border-border rounded-md p-2 flex items-start gap-2">
                            <MapPin className="size-3 mt-0.5" />
                            <div>
                                Geofence active: <span className="font-mono">{policy.geofence.latitude?.toFixed(4)}, {policy.geofence.longitude?.toFixed(4)}</span> · radius {policy.geofence.radius_meters}m
                                {policy.geofence.allow_remote_clockin ? " · remote clock-in allowed" : " · remote disabled"}
                            </div>
                        </div>
                    )}

                    {attendance?.time_in && (
                        <div className="border border-emerald-200 bg-emerald-50 text-emerald-800 rounded-md p-2 text-xs flex items-center gap-2" data-testid="already-in">
                            <CheckCircle2 className="size-3" />
                            Clocked in at {formatDateTime(attendance.time_in)}{attendance.time_out ? ` · clocked out at ${formatDateTime(attendance.time_out)}` : ""}
                            {attendance.work_from_home ? " · working from home" : ""}
                            {breaks.length ? ` · ${breaks.length} break${breaks.length === 1 ? "" : "s"}${onBreak ? " (on break now)" : ""}` : ""}
                        </div>
                    )}

                    {!hasClockedIn && (
                        <label className="flex items-center gap-2 text-sm border border-border rounded-md p-2.5 cursor-pointer">
                            <input type="checkbox" checked={wfh} onChange={(e) => setWfh(e.target.checked)} data-testid="wfh-checkbox" />
                            <span><span className="font-medium">I am working from home today</span>
                                <span className="block text-[11px] text-muted-foreground">Marked on your attendance. Your hours count the same as any other day.</span></span>
                        </label>
                    )}

                    <div className="flex gap-2">
                        {!hasClockedIn && (
                            <button onClick={() => submit("in")} disabled={!ready || !selfieReady || busy} className="flex-1 h-12 rounded-md bg-primary text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2" data-testid="clock-in-btn">
                                <Smartphone className="size-5" /> {busy ? "Clocking in…" : "Clock In"}
                            </button>
                        )}
                        {hasClockedIn && !hasClockedOut && (
                            <button onClick={() => breakAction(onBreak ? "out" : "in")} disabled={busy} className={`flex-1 h-12 rounded-md font-semibold disabled:opacity-50 flex items-center justify-center gap-2 ${onBreak ? "bg-emerald-600 text-white" : "border border-border bg-background"}`} data-testid={onBreak ? "break-out-btn" : "break-in-btn"}>
                                <Coffee className="size-5" /> {onBreak ? "End Break" : "Start Break"}
                            </button>
                        )}
                        {hasClockedIn && !hasClockedOut && (
                            <button onClick={() => submit("out")} disabled={!ready || busy} className="flex-1 h-12 rounded-md bg-gray-700 text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2" data-testid="clock-out-btn">
                                <LogOut className="size-5" /> {busy ? "Clocking out…" : "Clock Out"}
                            </button>
                        )}
                    </div>
                </div>
            </Surface>

            <Dialog open={showCam} onOpenChange={setShowCam}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>Take a selfie</DialogTitle></DialogHeader>
                    <video ref={videoRef} className="w-full rounded-md bg-black aspect-square object-cover" muted playsInline></video>
                    <button onClick={captureSelfie} className="h-10 rounded-md bg-primary text-white font-medium" data-testid="capture-selfie-btn">Capture</button>
                </DialogContent>
            </Dialog>
        </div>
    );
}
